import {inject, observer} from "mobx-react";
import {Image, Modal} from "react-bootstrap";
import logoLight from "../../img/official_logo.svg";
import logoDark from "../../img/official_logo_dark.svg";
import ChatSession from "../ChatSession";
import ChatDownloadMenu from "../menus/ChatDownloadMenu";
import {Tooltip} from "react-tooltip";
import {AGENT_ANALYST, ROLE_ASSISTANT} from "../../llm/LLMClient";
import {useEffect, useState, useRef} from "react";
import {Rnd} from "react-rnd";

// Chat session in context of a specific search
export const ChatModal = ({userStore, appStatusStore, isOpen, onHide, searchContextKey, historyId, ...chatSessionProps}) => {
  const [downloadContext, setDownloadContext] = useState(null);
  // Calculate initial width in pixels (2/3 of viewport)
  const initialWidth = Math.round(window.innerWidth * (2/3));
  // Calculate initial centered position
  const initialX = Math.round((window.innerWidth - initialWidth) / 2);
  const [size, setSize] = useState({ width: initialWidth, height: 'auto' });
  const [position, setPosition] = useState({ x: initialX, y: 70 });
  const [centered, setCentered] = useState(true);
  const modalContentRef = useRef(null);

  // Detect theme
  const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
  const logo = isDarkMode ? logoDark : logoLight;

  // Only enable download button if assistant has generated at least one full response
  const hasConversation = downloadContext?.client?.turns?.some(turn =>
    turn.role === ROLE_ASSISTANT && !turn.partial
  ) || false;

  // Mark as not-centered after first open (position already set in state)
  useEffect(() => {
    if (isOpen && centered) {
      setCentered(false);
    }
  }, [isOpen, centered]);

  // Reset centered state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCentered(true);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="chat-modal-wrapper">
      <div className="modal-backdrop fade show" onClick={onHide} />
      <Rnd
        size={{ width: size.width, height: 'auto' }}
        position={position}
        onDragStop={(e, d) => {
          // Keep modal on screen but allow it to go to edges
          const width = typeof size.width === 'number' ? size.width : parseFloat(size.width);

          const newX = Math.max(-width + 100, Math.min(d.x, window.innerWidth - 100));
          const newY = Math.max(0, Math.min(d.y, window.innerHeight - 100));

          setPosition({ x: newX, y: newY });
        }}
        onResizeStop={(e, direction, ref, delta, position) => {
          setSize({
            width: ref.style.width,
            height: ref.style.height
          });
          setPosition(position);
        }}
        minWidth="400px"
        minHeight="250px"
        maxHeight="80vh"
        dragHandleClassName="modal-header"
        disableDragging={false}
        enableResizing={{
          top: false,
          right: true,
          bottom: false,
          left: true,
          topRight: false,
          bottomRight: true,
          bottomLeft: true,
          topLeft: false
        }}
        className="chat-modal-rnd"
      >
        <div className="modal-dialog ai-modal" ref={modalContentRef}>
          <div className="modal-content">
            <div className="modal-header" style={{ cursor: 'move' }}>
              <div className="modal-title">
                <Image className="logo" src={logo} />
                <span className="title">Large Language Model (LLM) Interface</span>
                {hasConversation && downloadContext && (
                  <ChatDownloadMenu className="chat-download-modal" {...downloadContext} />
                )}
              </div>
              <button
                type="button"
                className="modal-close-btn"
                aria-label="Close"
                onClick={onHide}
                title="Close"
              >
                <span className="glyphicon glyphicon-remove" aria-hidden="true" />
              </button>
            </div>
            <div className="modal-body" autoFocus={true}>
              <ChatSession
                useTools={true}
                allowAttachments={true}
                /*showWelcome={true}*/
                userStore={userStore}
                appStatusStore={appStatusStore}
                sessionAgent={AGENT_ANALYST}
                searchContextKey={searchContextKey}
                historyId={historyId}
                historyType="search"
                onDownloadContextChange={setDownloadContext}
                modalContentRef={modalContentRef}
                {...chatSessionProps}
                tooltipID={"mtt"}
              />
            </div>
          </div>
        </div>
      </Rnd>
      <Tooltip id="mtt"/>
    </div>
  );
};

export default inject("userStore", "appStatusStore")(observer(ChatModal));
